import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { TargetId } from '@business/entities/programs';
import { CommentId, CommentResponse, CommentFileResponse } from '@business/entities/comments';
import { ProgramDiscussAPI } from '@business/api/program_discuss';

export class ProgramDiscussBloc extends BaseBloc {
  private readonly programCommentRepo = new Repository<CommentResponse>({
    getItemId: item => item.id,
  });

  readonly comments = this.programCommentRepo.items;
  readonly allComments$ = this.programCommentRepo.items$;
  readonly selectComment = this.programCommentRepo.selectItem;

  onReset() {
    this.programCommentRepo.reset();
  }

  fetchComments = (targetId: TargetId, fullResponse?: boolean) => {
    return ProgramDiscussAPI.fetchComments(targetId, fullResponse).pipe(
      this.programCommentRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_comments_error') }),
    );
  };

  fetchComment = (id: CommentId) => {
    return ProgramDiscussAPI.fetchComment(id).pipe(
      this.programCommentRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_comments_error') }),
    );
  };

  createComment = (data: Partial<CommentResponse>) => {
    return ProgramDiscussAPI.createComment(data).pipe(
      this.programCommentRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_comment'),
      }),
    );
  };

  updateComment = (id: CommentId, data: Partial<CommentResponse>) => {
    return ProgramDiscussAPI.updateComment(id, data).pipe(
      this.programCommentRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_comment'),
      }),
    );
  };

  deleteComment = (id: CommentId) => {
    return showDeleteConfirmation('Delete Comment', 'Do you really want to remove this Comment?').pipe(
      switchMap(() => ProgramDiscussAPI.deleteComment(id)),
      this.programCommentRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_comment'),
      }),
    );
  };

  createCommentFile = (data: Partial<CommentFileResponse>) => {
    return ProgramDiscussAPI.createCommentFile(data).pipe(
      this.programCommentRepo.ops.reset(),
      handleMessage({ type: requestMessage('create_comment_file') }),
    );
  };
}
